import React, {
  type ChangeEvent,
  useContext,
  useReducer,
  useRef,
  useState,
  type FocusEvent,
} from "react";
import {
  EuiSelect,
  type EuiSelectOption,
  EuiFlexItem,
  EuiFormRow,
  EuiFieldText,
  EuiFieldNumber,
  EuiComboBox,
  EuiRadioGroup,
  type EuiRadioGroupOption,
  type EuiComboBoxOptionOption,
  EuiTextArea,
  EuiMarkdownEditor,
  getDefaultEuiMarkdownParsingPlugins,
  EuiText,
  EuiMarkdownFormat,
  EuiSteps,
  EuiSpacer,
  EuiTextColor,
  EuiFlexGroup,
  EuiButtonIcon,
  copyToClipboard,
} from "@elastic/eui";
import { EvaluationContext, useAnketa } from "../context_provider";
import {
  OpenQuestion,
  OptionsQuestion,
  DateQuestion,
  NumericQuestion,
  SuggestionsQuestion,
  type TextMessage,
  type Question,
  OptionsValidator,
  type NumericValidator,
  type EvaluatorValue,
  type TextValidator,
  ValidatorType,
  TextValidatorType,
  ComplexQuestion,
  MultiSelectOptionsQuestion,
  CommentQuestion,
} from "anketa-core";
import { SuggestionsQuestionEditor } from "./suggestions_question_editor";
import { DateQuestionEditor } from "./date_question_editor";

import { remarkMermaidPlugin } from "../question_card/mermaid_plugin";
import { remarkSimplePlantumlPlugin } from "../question_card/plantuml_plugin";
import { ComplexQuestionEditor } from "./complex_question_editores/complex_question_editor";
import { PhoneInput } from "./phone_input";
import { CustomerEmailInput } from "./customer_email_input";
import { EuiContainedStepProps } from "@elastic/eui/src/components/steps/steps";
import moment from "moment";
import { CommentQuestionField } from "./comment/command_question";
import { useToast } from "src/notifications";
import { useAnketaI18n } from "src/internationalization";
import { useColorMode } from "src/theme";
import { isAnswerMissing, questionErrors } from "../issues";

export interface QuestionCardProps {
  question: Question<EvaluatorValue>;
}

export const CopyButton = ({ value }: { value: string }): JSX.Element => {
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyToClipboard(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    addToast({
      title: translate("anketa.helper.copy.success.title"),
      color: "success",
      text: translate("anketa.helper.copy.success.text"),
    });
  };

  return (
    <EuiButtonIcon
      iconType={copied ? "check" : "copy"}
      onClick={handleCopy}
      aria-label={translate("anketa.questionnaire.copy.aria-label", {
        defaultMessage: "Copy to clipboard",
      })}
      color={copied ? "success" : "primary"}
      display="empty"
    />
  );
};

/**
 * A computed / read-only answer: the value as data (monospaced) with a copy
 * button after it, or a dash when there is nothing yet - instead of an empty
 * gap next to a copy button that would copy nothing.
 */
const ReadonlyValue = ({ value }: { value: string }): JSX.Element => {
  if (value.trim() === "") {
    return (
      <EuiText size="s" color="subdued">
        <span aria-label="-">—</span>
      </EuiText>
    );
  }
  return (
    <EuiFlexGroup
      direction="row"
      gutterSize="xs"
      alignItems="center"
      responsive={false}
    >
      <EuiFlexItem grow={false} style={{ minWidth: 0 }}>
        <EuiText size="s">
          <span className="aqReadonlyValue">{value}</span>
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <CopyButton value={value} />
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};

/**
 * Multi-select editor. Declared at module level: a component class declared
 * inside QuestionCard is a new type on every render, so React remounted it on
 * each keystroke elsewhere and threw its selection state away.
 */
const MultiQuestionSelect = ({
  question,
  setValue,
  invalid,
}: {
  question: MultiSelectOptionsQuestion;
  setValue: (val: EvaluatorValue, revaluate?: boolean) => void;
  invalid: boolean;
}): JSX.Element => {
  const ctx = useAnketa();

  const opts: Array<EuiComboBoxOptionOption<string>> = [];
  question.getOptions(ctx).forEach((v: TextMessage, k: string) => {
    opts.push({ value: k, label: ctx.i18nContext.render(v) });
  });

  // Derived from the live fact on every render - no copy that can go stale.
  const factValues = (question.fact.raw as string[] | undefined) ?? [];
  const selectedOptions = factValues.map((value) => {
    const option = opts.find((opt) => opt.value === value);
    return option
      ? { value: option.value, label: option.label }
      : { value, label: value };
  });

  const commit = (options: Array<EuiComboBoxOptionOption<string>>): void => {
    const values = options
      .map((option) => option.value)
      .filter((value): value is string => value !== undefined);
    setValue(values as unknown as EvaluatorValue, true);
  };

  const onCreateOption = (
    searchValue: string,
    flattenedOptions: EuiComboBoxOptionOption<string>[] = [],
  ): void => {
    const normalized = searchValue.trim().toLowerCase();
    if (!normalized) {
      return;
    }
    if (
      flattenedOptions.findIndex(
        (option) => option.label.trim().toLowerCase() === normalized,
      ) === -1
    ) {
      commit([...selectedOptions, { value: normalized, label: searchValue }]);
    }
  };

  return (
    <EuiComboBox
      placeholder={ctx.i18nContext.render(question.placeholder)}
      options={opts}
      selectedOptions={selectedOptions}
      onChange={commit}
      onCreateOption={onCreateOption}
      isClearable={true}
      isInvalid={invalid}
      isDisabled={question.readonly}
      fullWidth
    />
  );
};

/** Editors that need the full row instead of the standard field width. */
const isWideQuestion = (question: Question<EvaluatorValue>): boolean => {
  if (question instanceof ComplexQuestion || question instanceof CommentQuestion) {
    return true;
  }
  if (question instanceof OpenQuestion && question.validator) {
    if (question.validator.type === ValidatorType.Markdown) {
      return true;
    }
    const validator = question.getValidator<TextValidator>();
    return question.validator.type === ValidatorType.Text && !!validator?.multiLine;
  }
  return false;
};

export const QuestionCard = ({ question }: QuestionCardProps): JSX.Element | null => {
  const ctx = useAnketa();
  const { translate } = useAnketaI18n();
  const evalCtx = useContext(EvaluationContext);
  const { colorMode } = useColorMode();

  // Every editor reads the LIVE fact (never a copy taken at mount), so a value
  // loaded, restored or computed after mount shows up - and blurring a field
  // can no longer write a stale empty copy back over the real answer.
  // Keystroke edits change the fact in place, so they force a re-render;
  // otherwise React would reset the controlled input to the old value.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  // Validation is shown once the user has committed a value (or the whole form
  // is revealed after a save), not on untouched fields.
  const [touched, setTouched] = useState(false);
  // Keystroke edits not yet committed by a blur.
  const pendingRef = useRef(false);

  /**
   * Single write path for every editor, including the sub-editors.
   * - `revaluate === false`: in-progress edit (keystroke). Updates the fact and
   *   re-renders this field only.
   * - otherwise: commit. Re-evaluates the catalog (visibility, computed
   *   values) and bumps the evaluation id, which re-renders the form and
   *   mirrors the answers into the local draft. Previously only an explicit
   *   `true` committed, so selects, dates, suggestions and every complex editor
   *   silently skipped re-evaluation and the draft.
   */
  function setValue(val: EvaluatorValue, revaluate?: boolean): void {
    if (revaluate === false) {
      question.setFactValue(val);
      pendingRef.current = true;
      forceRender();
      return;
    }
    const before = question.fact.raw;
    question.setFactValue(val);
    const changed = pendingRef.current || question.fact.raw !== before;
    pendingRef.current = false;
    setTouched(true);
    if (!changed) {
      // e.g. tabbing through a field: nothing to re-evaluate or save.
      forceRender();
      return;
    }
    if (!question.validate(ctx).hasError) {
      ctx.reevaluate();
    }
    evalCtx.nextEvaluationId();
  }

  const showIssues = touched || evalCtx.revealIssues;
  const missing = showIssues && isAnswerMissing(question);
  const errors = showIssues ? questionErrors(question, ctx) : [];
  const invalid = missing || errors.length > 0;
  if (missing) {
    errors.push(
      translate("anketa.questionnaire.validation.required", {
        defaultMessage: "Please answer this question.",
      }),
    );
  }

  function getOpenQuestionEditor(question: OpenQuestion): JSX.Element {
    if (
      question.validator &&
      question.validator.type === ValidatorType.Markdown
    ) {
      const parsingList = getDefaultEuiMarkdownParsingPlugins();
      parsingList.splice(2, 1);
      /*
      const emoticonPlugin = parsingList[2]
      if (emoticonPlugin) {
        (emoticonPlugin as any[])[1].emoticon = false
      }
      */
      // parsingList.push(remarkMermaidPlugin)
      parsingList.unshift([remarkSimplePlantumlPlugin, {}]);
      parsingList.unshift([
        remarkMermaidPlugin,
        {
          key: question.path.replaceAll(".", "_"),
          // Diagrams are rendered to SVG with baked-in colours, so follow the
          // active colour mode or they stay light in dark mode.
          theme: colorMode === "dark" ? "dark" : "default",
        },
      ]);

      if (question.readonly === true) {
        return (
          <EuiMarkdownFormat
            aria-label={ctx.i18nContext.render(question.description)}
            parsingPluginList={parsingList}
          >
            {question.fact.toString()}
          </EuiMarkdownFormat>
        );
      } else {
        return (
          <EuiMarkdownEditor
            style={{ width: "100%" }}
            aria-label={ctx.i18nContext.render(question.title)}
            parsingPluginList={parsingList}
            editorId={question.path}
            placeholder={ctx.i18nContext.render(question.placeholder)}
            initialViewMode="editing"
            value={question.fact.toString()}
            onChange={(value: string) => {
              setValue(value, false);
            }}
            // Commit when focus leaves the editor; before, markdown answers
            // were never re-evaluated nor written to the local draft.
            onBlur={() => {
              setValue(question.fact.toString(), true);
            }}
          />
        );
      }
    }

    if (question.validator && question.validator.type === ValidatorType.Text) {
      let lines = 1;
      const validator = question.getValidator<TextValidator>();

      // Customer e-mail input: free local part + domain restricted to the
      // questionnaire's assigned (non-internal) customer organizations.
      if (validator?.subType === TextValidatorType.CustomerEmail) {
        return (
          <CustomerEmailInput
            value={question.fact.toString()}
            onChange={(v) => {
              setValue(v, false);
            }}
            onBlur={(v) => setValue(v, true)}
            invalid={invalid}
            readOnly={question.readonly}
            placeholder={ctx.i18nContext.render(question.placeholder)}
          />
        );
      }

      // Modern phone input (country dial-code dropdown + national number).
      if (validator?.subType === TextValidatorType.PhoneNumber) {
        return (
          <PhoneInput
            value={question.fact.toString()}
            onChange={(v) => {
              setValue(v, false);
            }}
            onBlur={(v) => setValue(v, true)}
            invalid={invalid}
            readOnly={question.readonly}
            placeholder={ctx.i18nContext.render(question.placeholder)}
          />
        );
      }

      if (validator?.multiLine) {
        lines = validator.maxLength / 80;
        if (lines > 6) {
          lines = 6;
        }

        return (
          <EuiTextArea
            name={question.path}
            placeholder={ctx.i18nContext.render(question.placeholder)}
            isInvalid={invalid}
            readOnly={question.readonly}
            fullWidth
            rows={Math.max(3, Math.ceil(lines))}
            value={question.fact.toString()}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              setValue(e.target.value, false);
            }}
            onBlur={(e: FocusEvent<HTMLTextAreaElement>) => {
              setValue(e.target.value, true);
            }}
          />
        );
      }
    }

    if (question.readonly === true) {
      return <ReadonlyValue value={question.fact.toString()} />;
    } else {
      return (
        <EuiFieldText
          name={question.path}
          placeholder={ctx.i18nContext.render(question.placeholder)}
          isInvalid={invalid}
          readOnly={question.readonly}
          fullWidth
          value={question.fact.toString()}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value, false);
          }}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            setValue(e.target.value, true);
          }}
          prepend={<CopyButton value={question.fact.toString()} />}
        />
      );
    }
  }

  function getNumericQuestionEditor(question: NumericQuestion): JSX.Element {
    const validator = question.getValidator<NumericValidator>();
    const min = validator?.min
      ? question.getValidator<NumericValidator>()?.min
      : undefined;
    const max = validator?.max
      ? question.getValidator<NumericValidator>()?.max
      : undefined;
    let val: number | undefined = question.fact.getFloatValue(ctx.i18nContext);
    if (isNaN(val)) {
      val = undefined;
    }

    if (question.readonly) {
      return <ReadonlyValue value={val === undefined ? "" : String(val)} />;
    } else {
      return (
        <EuiFieldNumber
          name={question.path}
          placeholder={ctx.i18nContext.render(question.placeholder)}
          isInvalid={invalid}
          min={min}
          max={max}
          fullWidth
          value={val}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value, false);
          }}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            setValue(e.target.value, true);
          }}
          prepend={<CopyButton value={question.fact.toString()} />}
        />
      );
    }
  }

  function getOptionsQuestionEditor(question: OptionsQuestion): JSX.Element {
    // Few options: show them all as radios (one click, themed, accessible).
    // More: a select; very long lists: a searchable combo box. The former
    // 4-7 option case was a hand-built HTML table with hardcoded light colours
    // that ignored read-only and broke in dark mode.
    const count = question.getOptions(ctx).size;
    if (count < 8) {
      return getOptionsQuestionRadio(question);
    }
    if (count > 250) {
      return getOptionsQuestionCombo(question);
    }
    return getOptionsQuestionSelect(question);
  }

  function getOptionsQuestionRadio(question: OptionsQuestion): JSX.Element {
    const opts = new Array<EuiRadioGroupOption>();
    question.getOptions(ctx).forEach((v: TextMessage, k: string) => {
      opts.push({
        id: question.path + "." + k,
        label: ctx.i18nContext.render(v),
      });
    });
    const raw = question.fact.raw;
    const selectedId =
      raw !== undefined && raw !== null && raw !== ""
        ? question.path + "." + raw.toString()
        : undefined;
    return (
      <EuiRadioGroup
        name={question.path}
        options={opts}
        idSelected={selectedId}
        disabled={question.readonly}
        onChange={(id) => {
          setValue(id.substring(question.path.length + 1), true);
        }}
      />
    );
  }

  function getOptionsQuestionSelect(question: OptionsQuestion): JSX.Element {
    const opts: EuiSelectOption[] = [];

    if (
      !question.required ||
      question.fact.raw === undefined ||
      question.fact.toString() === ""
    ) {
      const option: EuiSelectOption = {
        value: "",
        text: ctx.i18nContext.render(
          ctx.messages.get(OptionsValidator.options_choose),
        ),
      };
      opts.push(option);
    }

    question.getOptions(ctx).forEach((v: TextMessage, k: string) => {
      const option: EuiSelectOption = {
        value: k,
        text: ctx.i18nContext.render(v),
      };
      opts.push(option);
    });

    return (
      <EuiSelect
        key={question.path}
        placeholder={ctx.i18nContext.render(question.placeholder)}
        options={opts}
        value={question.fact.raw?.toString() ?? ""}
        fullWidth
        isInvalid={invalid}
        disabled={question.readonly}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          setValue(e.target.value, true);
        }}
      />
    );
  }

  function getOptionsQuestionCombo(question: OptionsQuestion): JSX.Element {
    const opts: Array<EuiComboBoxOptionOption<string>> = [];
    const sel: Array<EuiComboBoxOptionOption<string>> = [];
    question.getOptions(ctx).forEach((v: TextMessage, k: string) => {
      const option: EuiComboBoxOptionOption<string> = {
        key: k,
        label: ctx.i18nContext.render(v),
      };
      if (k === question.fact.raw?.toString()) {
        sel.push(option);
      }
      opts.push(option);
    });
    return (
      <EuiComboBox<string>
        key={question.path}
        placeholder={ctx.i18nContext.render(question.placeholder)}
        options={opts}
        selectedOptions={sel}
        singleSelection={{ asPlainText: true }}
        fullWidth
        isInvalid={invalid}
        isDisabled={question.readonly}
        onChange={(options: Array<EuiComboBoxOptionOption<string>>) => {
          if (options.length > 0 && options[0].key !== undefined) {
            setValue(options[0].key, true);
          } else {
            setValue("", true);
          }
        }}
      />
    );
  }

  function getEditor(question: Question<EvaluatorValue>): JSX.Element {
    if (question instanceof OpenQuestion) {
      return getOpenQuestionEditor(question);
    }
    if (question instanceof DateQuestion) {
      return (
        <DateQuestionEditor
          question={question}
          setValue={setValue}
          invalid={invalid}
        />
      );
    }
    if (question instanceof SuggestionsQuestion) {
      return (
        <SuggestionsQuestionEditor
          question={question}
          setValue={setValue}
          invalid={invalid}
        />
      );
    }
    if (question instanceof OptionsQuestion) {
      return getOptionsQuestionEditor(question);
    }
    if (question instanceof NumericQuestion) {
      return getNumericQuestionEditor(question);
    }
    if (question instanceof ComplexQuestion) {
      if (question._id === "status") {
        const firstSetOfSteps: EuiContainedStepProps[] = [
          {
            title: translate("anketa.questionnaire.status.recording.title", {
              defaultMessage: "Recording the data",
            }),
            children: (
              <p>
                {(question.fact?.raw as any)?.questionary === "0"
                  ? translate(
                      "anketa.questionnaire.status.recording.complete",
                      {
                        defaultMessage:
                          "You have entered all the data we need from you.",
                      },
                    )
                  : translate(
                      "anketa.questionnaire.status.recording.pending",
                      {
                        defaultMessage:
                          "We need further data from you to continue.",
                      },
                    )}
              </p>
            ),
            status:
              (question.fact?.raw as any)?.questionary === "0"
                ? "complete"
                : "warning",
          },
          {
            title: translate("anketa.questionnaire.status.ordered.title", {
              defaultMessage: "Server ordered?",
            }),
            children: (
              <p>
                {translate("anketa.questionnaire.status.ordered.body", {
                  defaultMessage: "We have ordered your servers!",
                })}
                {(question.fact?.raw as any)?.server &&
                  translate("anketa.questionnaire.status.ordered.delivery", {
                    values: {
                      date: moment(
                        (question.fact?.raw as any)?.server,
                      ).format("MMM Do YYYY"),
                      relative: moment((question.fact?.raw as any)?.server)
                        .endOf("day")
                        .fromNow(),
                    },
                    defaultMessage:
                      "Delivery is expected to take place on {{date}} ({{relative}}).",
                  })}
              </p>
            ),
            status:
              (question.fact?.raw as any)?.serverordered === "0"
                ? "complete"
                : "incomplete",
          },
          {
            title:
              (question.fact?.raw as any)?.serverval === "0"
                ? translate("anketa.questionnaire.status.arrived.title.arrived", {
                    defaultMessage: "The servers have arrived.",
                  })
                : (question.fact?.raw as any)?.serverordered === "0"
                  ? translate(
                      "anketa.questionnaire.status.arrived.title.onway",
                      {
                        defaultMessage:
                          "The servers are still on their way to us.",
                      },
                    )
                  : translate(
                      "anketa.questionnaire.status.arrived.title.toorder",
                      {
                        defaultMessage:
                          "The servers still have to be ordered.",
                      },
                    ),
            children: (
              <p>
                {(question.fact?.raw as any)?.serverval === "0"
                  ? translate("anketa.questionnaire.status.arrived.body", {
                      defaultMessage:
                        "Now that the servers are with us, we get to work on deploying the appliance as quickly as possible.",
                    })
                  : ""}
              </p>
            ),
            status:
              (question.fact?.raw as any)?.serverval === "0"
                ? "complete"
                : (question.fact?.raw as any)?.serverordered === "0"
                  ? "loading"
                  : "incomplete",
          },
          {
            title: translate(
              "anketa.questionnaire.status.provisioning.title",
              {
                defaultMessage: "Server provisioning",
              },
            ),
            children: (
              <p>
                {(question.fact?.raw as any)?.serversetup === "0"
                  ? translate(
                      "anketa.questionnaire.status.provisioning.ready",
                      {
                        defaultMessage:
                          "The appliance is now ready to be installed on your premises.",
                      },
                    )
                  : (question.fact?.raw as any)?.serverval === "0"
                    ? (question.fact?.raw as any)?.questionary === "0"
                      ? translate(
                          "anketa.questionnaire.status.provisioning.preparing",
                          {
                            defaultMessage:
                              "We are still preparing your opsanio appliance for you.",
                          },
                        )
                      : translate(
                          "anketa.questionnaire.status.provisioning.fillData",
                          {
                            defaultMessage:
                              "Please fill in all data so that we can proceed.",
                          },
                        )
                    : translate(
                        "anketa.questionnaire.status.provisioning.waiting",
                        {
                          defaultMessage:
                            "We are still waiting for your server.",
                        },
                      )}
              </p>
            ),
            status:
              (question.fact?.raw as any)?.serversetup === "0"
                ? "complete"
                : (question.fact?.raw as any)?.serverval === "0"
                  ? (question.fact?.raw as any)?.questionary === "0"
                    ? "loading"
                    : "warning"
                  : "disabled",
          },
          {
            title: translate(
              "anketa.questionnaire.status.installation.title",
              {
                defaultMessage: "Installation in your surroundings.",
              },
            ),
            children: (
              <p>
                {(question.fact?.raw as any)?.serverinval === "0"
                  ? translate(
                      "anketa.questionnaire.status.installation.done",
                      {
                        defaultMessage:
                          "The servers have been successfully installed!",
                      },
                    )
                  : (question.fact?.raw as any)?.serverin
                    ? translate(
                        "anketa.questionnaire.status.installation.scheduled",
                        {
                          values: {
                            date: moment(
                              (question.fact?.raw as any)?.serverin,
                            ).format("MMM Do YYYY"),
                            relative: moment(
                              (question.fact?.raw as any)?.serverin,
                            )
                              .endOf("day")
                              .fromNow(),
                          },
                          defaultMessage:
                            "We look forward to installing the servers at your premises on {{date}} ({{relative}}).",
                        },
                      )
                    : (question.fact?.raw as any)?.serversetup === "0"
                      ? translate(
                          "anketa.questionnaire.status.installation.appointment",
                          {
                            defaultMessage:
                              "Almost done! We still have to make an appointment to install the servers at your premises.",
                          },
                        )
                      : translate(
                          "anketa.questionnaire.status.installation.toProvision",
                          {
                            defaultMessage:
                              "The servers still have to be provisioned.",
                          },
                        )}
              </p>
            ),
            status:
              (question.fact?.raw as any)?.serverinval === "0"
                ? "complete"
                : (question.fact?.raw as any)?.serverin
                  ? `incomplete`
                  : (question.fact?.raw as any)?.serversetup === "0"
                    ? "warning"
                    : "disabled",
          },
        ];
        return (
          <>
            {!question.readonly && (
              <ComplexQuestionEditor
                question={question}
                setValue={setValue}
                invalid={invalid}
              ></ComplexQuestionEditor>
            )}
            <EuiSpacer />
            <EuiSteps steps={firstSetOfSteps} />
          </>
        );
      } else {
        return (
          <ComplexQuestionEditor
            question={question}
            setValue={setValue}
            invalid={invalid}
          ></ComplexQuestionEditor>
        );
      }
    } else if (question instanceof CommentQuestion) {
      return (
        <EuiFlexGroup>
          <EuiFlexItem>
            <CommentQuestionField
              props={question}
              value={question.fact.toString()}
              setValue={(comment: string) => {
                setValue(comment, true);
              }}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      );
    }
    if (question instanceof MultiSelectOptionsQuestion) {
      return (
        <MultiQuestionSelect
          question={question}
          setValue={setValue}
          invalid={invalid}
        />
      );
    }
    return <pre>Unsupported Question Type</pre>;
  }

  // Hidden questions (and their follow-ups) are not rendered at all. They used
  // to be rendered and then hidden with display:none one effect later, which
  // flashed every hidden field on each re-evaluation.
  if (!question.visible) {
    return null;
  }

  // `required` defaults to true in anketa-core, so nearly every field is
  // required: marking each one is noise. Only the exceptions are marked.
  const title = ctx.i18nContext.render(question.title);
  const label = question.required ? (
    title
  ) : (
    <>
      {title}{" "}
      <EuiTextColor color="subdued">
        {translate("anketa.questionnaire.field.optional", {
          defaultMessage: "(optional)",
        })}
      </EuiTextColor>
    </>
  );

  return (
    <EuiFlexItem
      grow={false}
      className={
        isWideQuestion(question) ? "aqQuestion aqQuestion--wide" : "aqQuestion"
      }
    >
      <EuiFormRow
        label={label}
        hasEmptyLabelSpace={false}
        helpText={ctx.i18nContext.render(question.help) || undefined}
        isInvalid={invalid}
        fullWidth={true}
        error={errors}
      >
        {getEditor(question)}
      </EuiFormRow>
      {question.description && (
        <EuiText size="s" color="subdued" className="aqQuestion__description">
          <EuiMarkdownFormat>
            {ctx.i18nContext.render(question.description)}
          </EuiMarkdownFormat>
        </EuiText>
      )}
      {ctx.childrenForItem(question)}
    </EuiFlexItem>
  );
};
